import React, { useEffect, useState, useRef } from "react";
import Player from "@vimeo/player";

const parseTime = (timeString) => {
  const match = timeString.match(/^(\d+)m(\d+s)?$|^(\d+)s$/);
  if (!match) return 0;

  const minutes = parseInt(match[1] || 0, 10);
  const secondsFromMinutes = match[2] ? parseInt(match[2].slice(0, -1), 10) : 0;
  const seconds = parseInt(match[3] || 0, 10);

  return minutes * 60 + secondsFromMinutes + seconds;
};

const extractTimeParams = (url) => {
  if (!url || typeof url !== 'string') {
    console.error('Invalid URL passed to extractTimeParams:', url);
    return { baseVideoId: '', startTime: null, endTime: null };
  }

  const hashIndex = url.indexOf("#");
  let baseVideoId = url;
  let startTime = null;
  let endTime = null;

  if (hashIndex !== -1) {
    const hash = url.substring(hashIndex + 1); // Get the part after #
    baseVideoId = url.substring(0, hashIndex); // Extract video ID without hash

    const params = new URLSearchParams(hash); // Use URLSearchParams to parse parameters
    if (params.has("t")) startTime = parseTime(params.get("t"));
    if (params.has("end")) endTime = parseTime(params.get("end"));
  }

  return { baseVideoId, startTime, endTime };
};

const VimeoPlayer = ({ videoId, autoplay = false }) => {
  const playerRef = useRef(null);
  const [clientReady, setClientReady] = useState(false); // Track client-side readiness
  const { baseVideoId, startTime, endTime: initialEndTime } = extractTimeParams(videoId);
  let endTime = initialEndTime;

  useEffect(() => {
    // Only proceed if on the client side (i.e., window is available)
    if (typeof window !== 'undefined') {
      setClientReady(true); // Mark client as ready
    }
  }, []);

  useEffect(() => {
    if (!clientReady || !playerRef.current) return;

    const player = new Player(playerRef.current, {
      id: baseVideoId,
      width: 640,
      autoplay: autoplay,
    });

    // If start time exists, set the video to start from that point
    if (startTime !== null) {
      player.setCurrentTime(startTime).catch(console.error);
    }

    // Pause video when the time exceeds endTime
    player.on("timeupdate", (data) => {
      if (endTime !== null && data.seconds >= endTime) {
        player.pause().catch(console.error);
      }
    });

    return () => {
      player.destroy();
    };
  }, [clientReady, baseVideoId, startTime]);

  return (
    <div ref={playerRef} />
  );
};

export default VimeoPlayer;
